import { useState } from 'react';
import {
  Checkbox,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { Link, useParams } from 'react-router';
import type { PtwIzolasyonSatiri, PtwPermitDetail } from '@isg/shared-types';
import {
  useEkipmanTeyidi,
  useIzinDetay,
  useIzinSecenekleri,
  useIzinUygunluk,
  useSahaCevrimiciIslemi,
  useSahaIslemi,
} from '../api/queries';
import {
  PTW_IZOLASYON_DURUM_ETIKET,
  PTW_ONAY_SONUC_ETIKET,
  PTW_SUPER_DURUM_ETIKET,
  SAPKA_ETIKET,
} from '../i18n/tr';
import { KapiSebebi } from '../sozlesme/KapiSebebi';
import { SahaKuyrukPaneli } from './saha-parts';
import {
  GecerlilikHucresi,
  PtwYapilandirmaBagi,
  UygunlukListesi,
  damga,
  durumRozeti,
  kisiEtiketi,
} from './izin-parts';
import { QueryStatus } from './parts';
import { ANLAM } from '../theme';
import { zamanGoster } from '../lib/format';

/**
 * SAHA YÜZEYİ (F3·S5 — tasarım tutanağı T2 envanteri). MOBİL ÖNCELİKLİ
 * responsive WEB'dir; React Native ayrı fazdır (kilitli karar).
 *
 * ALTI ADIM: işbaşı doğrulaması (aktife geçiş / askıdan dönüş) · askı ·
 * devir (başlat + kabul) · izolasyon + kişisel kilit (zorla dahil) · "işim
 * bitti" ve saha kontrolü · ekipman kimlik teyidi (K11).
 *
 * İKİ KURAL, ekranda hiç tekrarlanmaz:
 *  1. **HER DÜĞME SUNUCUDAN** (§2.16): `detay.yetkiler` + izolasyon satırının
 *     kendi yüklemleri. Ekran "kim ne yapabilir"i hesaplamaz.
 *  2. **İŞBAŞI KAPISI UYGUNLUKTAN**: aktife geçiş/askıdan dönüş düğmeleri
 *     uygunluk TEMİZ değilken çıkmaz — kalemler kalem kalem gösterilir (K8).
 *     Sunucu da aynı kontrolü koşar (422); ekran erken söyler, karar vermez.
 */
export function IzinSahaScreen(): React.JSX.Element {
  const { permitId } = useParams();
  const detay = useIzinDetay(permitId ?? null);
  const uygunluk = useIzinUygunluk(permitId ?? null);

  return (
    <Stack maw={720}>
      <QueryStatus queries={[detay]} />
      <SahaKuyrukPaneli />
      {/* SON BİLİNEN VERİYLE ÇİZ: saha ekranı ağ kesildiğinde BOŞALMAZ —
          kuyruğa adım eklemek için kartların ayakta kalması gerekir
          (`data` varsa çiz; tazeleme hatası kartları düşürmez). */}
      {detay.data && (
        <>
          <Card withBorder>
            <Stack gap={6}>
              <Title order={4}>
                {detay.data.permit.workplaceAd} — {detay.data.permit.locationAd}
              </Title>
              <Group gap="xs">
                {durumRozeti(detay.data.permit)}
                <Badge variant="outline">
                  {PTW_SUPER_DURUM_ETIKET[detay.data.permit.superDurum]}
                </Badge>
                {damga(detay.data.permit)}
              </Group>
              <GecerlilikHucresi permit={detay.data.permit} />
              <Text size="sm">Güncel sorumlu: {detay.data.guncelSorumluAd ?? '—'}</Text>
              <Group gap="xs">
                <Button
                  size="xs"
                  variant="default"
                  component={Link}
                  to={`/izinler/${detay.data.permit.id}`}
                >
                  Tam izin kartı
                </Button>
                <Button
                  size="xs"
                  variant="default"
                  component={Link}
                  to={`/izinler/${detay.data.permit.id}/qr`}
                >
                  QR kartı
                </Button>
              </Group>
            </Stack>
          </Card>

          <IsbasiKarti detay={detay.data} uygunlukSorgusu={uygunluk} />
          <DevirKarti detay={detay.data} />
          <IzolasyonKarti detay={detay.data} />
          <EkipmanTeyitKarti detay={detay.data} />
          <KapanisKarti detay={detay.data} />
        </>
      )}
    </Stack>
  );
}

/**
 * İŞBAŞI DOĞRULAMASI — aktife geçiş ve askıdan dönüş AYNI kontrolün iki
 * anıdır (§2.5). Uygunluk kirliyken geçiş düğmesi ÇIKMAZ; kalemler K8
 * üçlüsüyle (hangi kayıt · hangi kural · ne yapılmalı) listelenir.
 *
 * ASKIYA ALMA kuyruk kapsamındadır (kapanış yönlü, güvenli); ASKIDAN DÖNÜŞ
 * değildir — canlı uygunluk kararı ister (bayat veriyle izin canlandırılmaz).
 */
function IsbasiKarti({
  detay,
  uygunlukSorgusu,
}: {
  detay: PtwPermitDetail;
  uygunlukSorgusu: ReturnType<typeof useIzinUygunluk>;
}): React.JSX.Element {
  const kuyruklu = useSahaIslemi();
  const cevrimici = useSahaCevrimiciIslemi();
  const [askiGerekce, setAskiGerekce] = useState('');
  const permitId = detay.permit.id;
  const uygun = uygunlukSorgusu.data?.uygun ?? false;
  const yetkiler = detay.yetkiler;

  return (
    <Card withBorder data-testid="saha-isbasi-karti">
      <Stack gap="xs">
        <Text fw={600}>İşbaşı</Text>
        <QueryStatus queries={[uygunlukSorgusu]} />
        {uygunlukSorgusu.isSuccess && (
          <UygunlukListesi sonuc={uygunlukSorgusu.data} baslik="İşbaşı doğrulaması" />
        )}
        <Group gap="xs">
          {/* KAPI: yetki (sunucu) ∧ uygunluk TEMİZ. Kirliyken düğme YOK —
              kullanıcı basıp 422 yemez, engeli okur. */}
          {yetkiler.aktifeGecirebilir && uygun && (
            <Button
              size="sm"
              data-testid="saha-aktife-gec"
              loading={cevrimici.isPending}
              onClick={() =>
                cevrimici.mutate({ permitId, path: `/api/ptw/permits/${permitId}/activate` })
              }
            >
              İşbaşı — aktife geçir
            </Button>
          )}
          {yetkiler.askidanDonebilir && uygun && (
            <Button
              size="sm"
              data-testid="saha-askidan-don"
              loading={cevrimici.isPending}
              onClick={() =>
                cevrimici.mutate({ permitId, path: `/api/ptw/permits/${permitId}/resume` })
              }
            >
              Askıdan dön
            </Button>
          )}
          {(yetkiler.aktifeGecirebilir || yetkiler.askidanDonebilir) && !uygun && (
            <Alert color={ANLAM.engel} role="alert" data-testid="saha-isbasi-engeli">
              Uygunluk temiz değil — engelleyen kalemler giderilmeden işbaşı yapılamaz.
            </Alert>
          )}
        </Group>

        {yetkiler.askiyaAlabilir && (
          <Stack gap={4}>
            <Textarea
              label="Askı gerekçesi"
              description="İsteğe bağlı; kayda geçer. Askı EKİBİN duraksamasıdır."
              value={askiGerekce}
              onChange={(e) => setAskiGerekce(e.currentTarget.value)}
              data-testid="saha-aski-gerekce"
              autosize
              minRows={2}
            />
            <Button
              size="sm"
              variant="light"
              color={ANLAM.dikkat}
              data-testid="saha-askiya-al"
              loading={kuyruklu.isPending}
              onClick={() =>
                kuyruklu.mutate({
                  tip: 'askiya_al',
                  permitId,
                  gerekce: askiGerekce.trim() === '' ? null : askiGerekce.trim(),
                })
              }
            >
              İşi askıya al
            </Button>
          </Stack>
        )}
        <IslemHatasi hata={kuyruklu.error ?? cevrimici.error} />
      </Stack>
    </Card>
  );
}

/**
 * VARDİYA DEVRİ (K1) — devir bir GEÇİŞ DEĞİLDİR: izin `active` kalır, kabul
 * gelmeden güncel sorumlu DEĞİŞMEZ. Kabul uygunluk motorundan geçer ve sonuç
 * kabul kaydına KANIT olarak yazılır (sunucuda).
 */
function DevirKarti({ detay }: { detay: PtwPermitDetail }): React.JSX.Element | null {
  const cevrimici = useSahaCevrimiciIslemi();
  // ✏️ TUR 10 / K2: devralan havuzu da iznin işyeriyle sınırlıdır. Kutu
  // ZORUNLU — kutusuz bir süzgeç, başka işyerinden çağrılan bir sorumluya
  // devri İMKÂNSIZ kılardı (§2.40: ulaşılamayan hedefi olan kontrol).
  const [tumHolding, setTumHolding] = useState(false);
  const secenekler = useIzinSecenekleri(
    detay.yetkiler.devirBaslatabilir ? detay.permit.workplaceId : null,
    tumHolding,
  );
  const [devralan, setDevralan] = useState<string | null>(null);
  const permitId = detay.permit.id;
  if (!detay.yetkiler.devirBaslatabilir && !detay.acikDevir) return null;

  return (
    <Card withBorder data-testid="saha-devir-karti">
      <Stack gap="xs">
        <Text fw={600}>Vardiya devri</Text>
        {detay.acikDevir && (
          <Alert color={ANLAM.bilgi} role="status">
            <Text size="sm">
              {detay.acikDevir.devredenAd} → {detay.acikDevir.devralanAd} ·{' '}
              {zamanGoster(detay.acikDevir.baslatmaAt)}
            </Text>
            <Text size="xs" c="dimmed">
              Kabul gelmeden sorumluluk devredende kalır.
            </Text>
          </Alert>
        )}
        {detay.acikDevir?.kabulEdebilirim && (
          <Button
            size="sm"
            data-testid="saha-devir-kabul"
            loading={cevrimici.isPending}
            onClick={() =>
              cevrimici.mutate({
                permitId,
                path: `/api/ptw/handovers/${detay.acikDevir!.id}/accept`,
              })
            }
          >
            Devri kabul et
          </Button>
        )}
        {detay.yetkiler.devirBaslatabilir && (
          <>
            <Select
              label="Devralan"
              placeholder="Kişi ve işveren seçin"
              searchable
              data={(secenekler.data?.sorumluAdaylari ?? []).map((aday) => ({
                value: aday.employmentId,
                // K2.2: devralan da saha girişi rozetiyle görünür — devir
                // kabulü zaten uygunluktan geçer (S3), rozet ONDAN ÖNCE söyler.
                label: `${kisiEtiketi(aday.personAd, aday.sahaGirisiGecerli)} — ${aday.isverenAd}`,
              }))}
              value={devralan}
              onChange={setDevralan}
              data-testid="saha-devralan"
            />
            <Checkbox
              size="sm"
              label="Tüm holdingdeki kişileri göster"
              checked={tumHolding}
              onChange={(olay) => setTumHolding(olay.currentTarget.checked)}
              data-testid="saha-devralan-tum-holding"
            />
            <Button
              size="sm"
              variant="light"
              disabled={devralan === null}
              data-testid="saha-devir-baslat"
              loading={cevrimici.isPending}
              onClick={() =>
                cevrimici.mutate({
                  permitId,
                  path: `/api/ptw/permits/${permitId}/handovers`,
                  govde: { devralanEmploymentId: devralan },
                })
              }
            >
              Devri başlat
            </Button>
          </>
        )}
        <IslemHatasi hata={cevrimici.error} />
      </Stack>
    </Card>
  );
}

/**
 * İZOLASYON + KİŞİSEL KİLİT (K5/K6). Zincir: kişisel kilitler çıkmadan
 * izolasyon kalkmaz → izolasyon kalkmadan kapanış onaylanamaz.
 *
 * KİLİDİ TAKAN KALDIRIR; ulaşılamayan kişi için KAYITLI ZORLA KALDIRMA
 * (izin veren onayı + gerekçe + kim kesti — üçü birlikte). Zorla yol
 * ÇEVRİMİÇİ ister: onaylayanın izin veren olduğu sunucuda doğrulanır.
 */
function IzolasyonKarti({ detay }: { detay: PtwPermitDetail }): React.JSX.Element {
  const kuyruklu = useSahaIslemi();
  const cevrimici = useSahaCevrimiciIslemi();
  const [nokta, setNokta] = useState('');
  const [kilitNo, setKilitNo] = useState('');
  const permitId = detay.permit.id;

  return (
    <Card withBorder data-testid="saha-izolasyon-karti">
      <Stack gap="xs">
        <Group gap="xs">
          <Text fw={600}>İzolasyon / LOTO</Text>
          {detay.izolasyonlar.some((iz) => iz.durum !== 'removed') && (
            <Badge color={ANLAM.dikkat}>
              {detay.izolasyonlar.filter((iz) => iz.durum !== 'removed').length} açık nokta
            </Badge>
          )}
        </Group>
        {detay.izolasyonlar.length === 0 && (
          <Text size="sm" c="dimmed">
            Kayıtlı izolasyon yok.
          </Text>
        )}
        {detay.izolasyonlar.map((iz) => (
          <IzolasyonSatiri key={iz.id} izolasyon={iz} permitId={permitId} />
        ))}

        {detay.yetkiler.izolasyonKapisi.yazabilir && (
          <Stack gap={4} mt="xs">
            <TextInput
              label="Yeni izolasyon noktası"
              placeholder="Örn. MCC-3 ana şalter"
              value={nokta}
              onChange={(e) => setNokta(e.currentTarget.value)}
              data-testid="saha-izolasyon-nokta"
            />
            <TextInput
              label="Kilit / etiket no"
              value={kilitNo}
              onChange={(e) => setKilitNo(e.currentTarget.value)}
              data-testid="saha-izolasyon-kilit-no"
            />
            <Button
              size="sm"
              variant="light"
              disabled={nokta.trim() === ''}
              data-testid="saha-izolasyon-uygula"
              loading={kuyruklu.isPending}
              onClick={() =>
                kuyruklu.mutate(
                  {
                    tip: 'izolasyon_uygula',
                    permitId,
                    izolasyonNoktasi: nokta.trim(),
                    kilitNo: kilitNo.trim() === '' ? undefined : kilitNo.trim(),
                  },
                  {
                    onSuccess: () => {
                      setNokta('');
                      setKilitNo('');
                    },
                  },
                )
              }
            >
              İzolasyonu uygula
            </Button>
          </Stack>
        )}
        {/* ✏️ K-97 (bulgu 97): genel kural cümlesi KİŞİSEL SEBEBİN yerine geçmez —
            kullanıcı kendi durumunu duyar ("bu izinde katılımcı değilsiniz" ·
            "izin … durumunda"). Metin sunucunun reddettiği cümledir. */}
        <KapiSebebi
          durum={detay.yetkiler.izolasyonKapisi}
          nesne="İzolasyon kaydını"
          fiil="açar"
          testId="saha-izolasyon-sebep"
        />
        <IslemHatasi hata={kuyruklu.error ?? cevrimici.error} />
      </Stack>
    </Card>
  );
}

function IzolasyonSatiri({
  izolasyon,
  permitId,
}: {
  izolasyon: PtwIzolasyonSatiri;
  permitId: string;
}): React.JSX.Element {
  const kuyruklu = useSahaIslemi();
  const cevrimici = useSahaCevrimiciIslemi();
  const [zorla, setZorla] = useState<{ lockId: string } | null>(null);

  return (
    <Card withBorder padding="xs" data-testid="saha-izolasyon-satiri">
      <Stack gap={6}>
        <Group gap="xs" justify="space-between">
          <Text size="sm" fw={600}>
            {izolasyon.izolasyonNoktasi}
          </Text>
          <Badge variant="light">{PTW_IZOLASYON_DURUM_ETIKET[izolasyon.durum]}</Badge>
        </Group>
        <Text size="xs" c="dimmed">
          Takan: {izolasyon.takanAd}
          {izolasyon.kilitNo ? ` · kilit ${izolasyon.kilitNo}` : ''} · açık kilit:{' '}
          {izolasyon.acikKilitSayisi}
        </Text>
        {izolasyon.kilitler.map((kilit) => (
          <Group key={kilit.id} gap="xs">
            <Text size="xs">
              {kilit.personAd}
              {kilit.benimMi ? ' (siz)' : ''} —{' '}
              {kilit.cikarmaAt
                ? `çıkarıldı ${zamanGoster(kilit.cikarmaAt)}${kilit.zorlaKaldirildi ? ' · ZORLA' : ''}`
                : 'takılı'}
            </Text>
            {kilit.cikarmaAt === null && kilit.benimMi && (
              <Button
                size="compact-xs"
                variant="default"
                data-testid="saha-kilit-cikar"
                loading={kuyruklu.isPending}
                onClick={() => kuyruklu.mutate({ tip: 'kilit_cikar', permitId, lockId: kilit.id })}
              >
                Kilidimi çıkar
              </Button>
            )}
            {/* K6 istisna yolu: kilit BAŞKASININ ve hâlâ takılı. Zorla
                kaldırma ÇEVRİMİÇİ ister — onaylayanın izin veren olduğu
                sunucuda doğrulanır. */}
            {kilit.cikarmaAt === null && !kilit.benimMi && (
              <Button
                size="compact-xs"
                color={ANLAM.engel}
                variant="light"
                data-testid="saha-kilit-zorla"
                onClick={() => setZorla({ lockId: kilit.id })}
              >
                Zorla kaldır
              </Button>
            )}
          </Group>
        ))}
        <Group gap="xs">
          {izolasyon.dogrulanabilir && (
            <Button
              size="compact-xs"
              variant="light"
              data-testid="saha-izolasyon-dogrula"
              loading={kuyruklu.isPending}
              onClick={() =>
                kuyruklu.mutate({ tip: 'izolasyon_dogrula', permitId, isolationId: izolasyon.id })
              }
            >
              Doğrula
            </Button>
          )}
          {izolasyon.kilitKapisi.yazabilir && (
            <Button
              size="compact-xs"
              variant="light"
              data-testid="saha-kilit-as"
              loading={kuyruklu.isPending}
              onClick={() =>
                kuyruklu.mutate({ tip: 'kilit_as', permitId, isolationId: izolasyon.id })
              }
            >
              Kişisel kilidimi as
            </Button>
          )}
          {izolasyon.kaldirilabilir && (
            <Button
              size="compact-xs"
              color={ANLAM.dikkat}
              data-testid="saha-izolasyon-kaldir"
              loading={cevrimici.isPending}
              onClick={() =>
                cevrimici.mutate({
                  permitId,
                  path: `/api/ptw/isolations/${izolasyon.id}/remove`,
                })
              }
            >
              İzolasyonu kaldır
            </Button>
          )}
        </Group>
        {/* ✏️ K-97: kilit kapısı satır başına — kaldırılmış noktada sebep
            gürültüdür (durum rozeti zaten söylüyor). */}
        {izolasyon.durum !== 'removed' && (
          <KapiSebebi
            durum={izolasyon.kilitKapisi}
            nesne="Kişisel kilidi"
            fiil="asar"
            testId="saha-kilit-sebep"
          />
        )}
        <IslemHatasi hata={kuyruklu.error ?? cevrimici.error} />
      </Stack>
      <ZorlaKaldirmaKutusu
        permitId={permitId}
        lockId={zorla?.lockId ?? null}
        onClose={() => setZorla(null)}
      />
    </Card>
  );
}

/** K6 — ZORLA KALDIRMA: üçü birlikte (izin veren onayı + gerekçe + kim
 *  kesti). DB CHECK de zorlar; yarım kayıt imkânsızdır. */
function ZorlaKaldirmaKutusu({
  permitId,
  lockId,
  onClose,
}: {
  permitId: string;
  lockId: string | null;
  onClose: () => void;
}): React.JSX.Element {
  const cevrimici = useSahaCevrimiciIslemi();
  const secenekler = useIzinSecenekleri(null);
  const [onaylayan, setOnaylayan] = useState('');
  const [kesen, setKesen] = useState('');
  const [gerekce, setGerekce] = useState('');
  void secenekler;

  return (
    <Modal opened={lockId !== null} onClose={onClose} title="Kilidi zorla kaldır">
      <Stack>
        <Text size="sm" c="dimmed">
          Kural: kilidi TAKAN kaldırır. Ulaşılamayan kişi için bu yol kayıtlıdır — izin verenin
          onayı, gerekçe ve kimin kestiği birlikte yazılır; raporda ayrı görünür.
        </Text>
        <TextInput
          label="İzin verenin kişi kimliği (uuid)"
          description="Onaylayanın iznin izin vereni olduğu sunucuda doğrulanır."
          value={onaylayan}
          onChange={(e) => setOnaylayan(e.currentTarget.value)}
          data-testid="zorla-onaylayan"
        />
        <TextInput
          label="Kesen kişi kimliği (uuid)"
          value={kesen}
          onChange={(e) => setKesen(e.currentTarget.value)}
          data-testid="zorla-kesen"
        />
        <Textarea
          label="Gerekçe"
          withAsterisk
          placeholder="Örn. kişiye ulaşılamadı"
          value={gerekce}
          onChange={(e) => setGerekce(e.currentTarget.value)}
          data-testid="zorla-gerekce"
        />
        <IslemHatasi hata={cevrimici.error} />
        <Button
          color={ANLAM.engel}
          data-testid="zorla-onayla"
          disabled={onaylayan.trim() === '' || kesen.trim() === '' || gerekce.trim() === ''}
          loading={cevrimici.isPending}
          onClick={() =>
            cevrimici.mutate(
              {
                permitId,
                path: `/api/ptw/isolation-locks/${lockId ?? ''}/remove`,
                govde: {
                  zorla: {
                    onaylayanPersonId: onaylayan.trim(),
                    kesenPersonId: kesen.trim(),
                    gerekce: gerekce.trim(),
                  },
                },
              },
              { onSuccess: onClose },
            )
          }
        >
          Zorla kaldır ve kaydet
        </Button>
      </Stack>
    </Modal>
  );
}

/**
 * K11 — EKİPMAN KİMLİK TEYİDİ: OPSİYONELDİR ve işbaşını BLOKLAMAZ. Kamera
 * taraması native yüzeyin işidir (mobil faz); burada okunan kod elle/harici
 * okuyucuyla girilir — kayıt ve sonuç cümlesi AYNI uçtan gelir.
 */
function EkipmanTeyitKarti({ detay }: { detay: PtwPermitDetail }): React.JSX.Element {
  const teyit = useEkipmanTeyidi();
  const kuyruklu = useSahaIslemi();
  const [kod, setKod] = useState('');
  return (
    <Card withBorder data-testid="saha-teyit-karti">
      <Stack gap="xs">
        <Text fw={600}>Ekipman kimlik teyidi</Text>
        <Text size="xs" c="dimmed">
          İsteğe bağlıdır: eşleşme kaydı düşer, işbaşını durdurmaz.
        </Text>
        <TextInput
          label="Okunan kod"
          placeholder="Ekipman QR kodu"
          value={kod}
          onChange={(e) => setKod(e.currentTarget.value)}
          data-testid="saha-teyit-kod"
        />
        <Button
          size="sm"
          variant="light"
          disabled={kod.trim() === ''}
          data-testid="saha-teyit-gonder"
          loading={teyit.isPending || kuyruklu.isPending}
          onClick={() => {
            // ÇEVRİMDIŞIYSA KUYRUĞA: tarama kuyruk kapsamındadır (kayıt
            // bloklamayan izdir). Çevrimiçiyken doğrudan gönderilir çünkü
            // sonucun CÜMLESİ sunucudan gelir ve hemen gösterilir.
            if (!navigator.onLine) {
              kuyruklu.mutate(
                { tip: 'ekipman_teyit', permitId: detay.permit.id, kod: kod.trim() },
                { onSuccess: () => setKod('') },
              );
              return;
            }
            teyit.mutate(
              { permitId: detay.permit.id, kod: kod.trim() },
              { onSuccess: () => setKod('') },
            );
          }}
        >
          Teyit et
        </Button>
        {teyit.data && (
          <Alert
            color={teyit.data.eslesti ? ANLAM.olumlu : ANLAM.dikkat}
            role="status"
            data-testid="saha-teyit-sonucu"
          >
            {teyit.data.mesaj}
          </Alert>
        )}
        {detay.sonTaramalar.length > 0 && (
          <Stack gap={2}>
            {detay.sonTaramalar.map((tarama) => (
              <Text key={tarama.id} size="xs" c="dimmed" data-testid="saha-tarama-satiri">
                {zamanGoster(tarama.taramaAt)} · {tarama.ekipmanAd ?? tarama.tarananKod} ·{' '}
                {tarama.eslesti ? 'eşleşti' : 'EŞLEŞMEDİ'} · {tarama.tarayanAd}
              </Text>
            ))}
          </Stack>
        )}
        <IslemHatasi hata={teyit.error ?? kuyruklu.error} />
      </Stack>
    </Card>
  );
}

/**
 * KAPANIŞ ADIMLARI — iki adımlıdır (Kapsam §16.7.2/5): önce güncel sorumlu
 * "işim bitti" der (kuyruk kapsamında), sonra saha kontrol yetkilisi sahayı
 * görüp kapanış onay turunu açar (çevrimiçi — yetki listesi kararı).
 *
 * ✏️ K-99 (bulgu 99): *Kapanışta* olan izne bakan ama saha kontrolü şapkası
 * olmayan kullanıcı kartı HİÇ görmüyordu — ne eylem ne sebep. Kart artık
 * kapanış aşamasında herkese çizilir: saha kontrolü beklerken
 * `sahaKontrolKapisi`nin sebebi (tenant şapka listesi + yapılandırma bağı),
 * kapanış onayı beklerken basamakların `kararKapisi` sebepleri. Karar izin
 * kartından verilir (İşlerim'le AYNI karar kutusu — ikinci yol açılmadı).
 */
function KapanisKarti({ detay }: { detay: PtwPermitDetail }): React.JSX.Element | null {
  const kuyruklu = useSahaIslemi();
  const cevrimici = useSahaCevrimiciIslemi();
  const permitId = detay.permit.id;
  const sahaKontrolKapisi = detay.yetkiler.sahaKontrolKapisi;
  const kapanisOnayi = detay.onayBasamaklari?.kapi === 'kapanis' ? detay.onayBasamaklari : null;
  if (!detay.yetkiler.isimBittiBildirebilir && sahaKontrolKapisi === null && kapanisOnayi === null)
    return null;

  return (
    <Card withBorder data-testid="saha-kapanis-karti">
      <Stack gap="xs">
        <Text fw={600}>Kapanış</Text>
        {detay.izolasyonlar.some((iz) => iz.durum !== 'removed') && (
          <Alert color={ANLAM.engel} role="alert">
            Açık izolasyon var — kapanış onayı verilemez (önce izolasyonlar kaldırılır).
          </Alert>
        )}
        {detay.yetkiler.isimBittiBildirebilir && (
          <Button
            size="sm"
            data-testid="saha-isim-bitti"
            loading={kuyruklu.isPending}
            onClick={() => kuyruklu.mutate({ tip: 'isim_bitti', permitId })}
          >
            İşim bitti
          </Button>
        )}
        {sahaKontrolKapisi?.yazabilir && (
          <Button
            size="sm"
            variant="light"
            data-testid="saha-kontrol"
            loading={cevrimici.isPending}
            onClick={() =>
              cevrimici.mutate({ permitId, path: `/api/ptw/permits/${permitId}/field-check` })
            }
          >
            Saha kontrolü yapıldı
          </Button>
        )}
        {sahaKontrolKapisi && !sahaKontrolKapisi.yazabilir && (
          <Stack gap={2}>
            <KapiSebebi
              durum={sahaKontrolKapisi}
              nesne="Saha kontrolünü"
              fiil="yapar"
              testId="saha-kontrol-sebep"
            />
            {sahaKontrolKapisi.sebep?.kod === 'sapka' && (
              <PtwYapilandirmaBagi etiket="İş izni yapılandırması — saha kontrolü şapka listesi" />
            )}
          </Stack>
        )}
        {kapanisOnayi && (
          <Stack gap={4} data-testid="saha-kapanis-onayi">
            <Text size="sm" fw={600}>
              Kapanış onayı
            </Text>
            {kapanisOnayi.basamaklar.map((b) => (
              <Group key={b.chainStepId} gap="xs" data-testid="saha-kapanis-basamagi">
                <Badge variant="light">
                  {b.stepAd} · {SAPKA_ETIKET[b.sapka]}
                </Badge>
                {b.karar ? (
                  <Text size="xs">
                    {PTW_ONAY_SONUC_ETIKET[b.karar.sonuc]} — {b.karar.onaylayanAd ?? '—'}
                  </Text>
                ) : b.kararKapisi.yazabilir ? (
                  <Button
                    size="compact-xs"
                    component={Link}
                    to={`/izinler/${permitId}`}
                    data-testid="saha-kapanis-karar"
                  >
                    Kararı izin kartında ver
                  </Button>
                ) : (
                  <KapiSebebi
                    durum={b.kararKapisi}
                    nesne="Bu basamağı"
                    fiil="onaylar"
                    testId={`saha-kapanis-sebep-${b.sira}`}
                  />
                )}
              </Group>
            ))}
          </Stack>
        )}
        <IslemHatasi hata={kuyruklu.error ?? cevrimici.error} />
      </Stack>
    </Card>
  );
}

/** Sunucu ret dilini AYNEN gösterir (K8 üçlüsü) — ekran cümle uydurmaz. */
function IslemHatasi({ hata }: { hata: Error | null }): React.JSX.Element | null {
  if (!hata) return null;
  return (
    <Text size="sm" c={ANLAM.engel} role="alert" data-testid="saha-islem-hatasi">
      {hata.message}
    </Text>
  );
}
